<script setup lang="ts">
import { amountRegex } from "~/constants/regex.constants";

const { minimumPrice } = useRuntimeConfig().public;
const { offerExpirationDays } = useRuntimeConfig().public;
const GFALExStore = useGFALExStore();
const emit = defineEmits(["close", "offer-set"]);
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});

const { getCollectibleHighestOffer } = useCollectiblesController();
const { t } = useI18n();
const { trackEvent } = useGAnalytics();
const userStore = useUserStore();
const isLoading = ref(false);
const errorMessageAmount = ref("");
const offerAmount = defineModel("offerAmount", { type: String });
const highestOffer = ref<number | null>();

const validateAmount = () => {
  if (GFALExStore.convertToUSD(offerAmount.value || "0") <= minimumPrice) {
    errorMessageAmount.value =
      t("errorMessages.transferInvalidMinimumPrice") +
      " " +
      Number(minimumPrice).toFixed(2) +
      " USD";
    return false;
  } else if (!offerAmount.value || Number(offerAmount.value) <= 0) {
    errorMessageAmount.value = t("errorMessages.transferInvalidAmount");
    return false;
  } else if (!amountRegex.test(offerAmount.value.toString())) {
    errorMessageAmount.value = t("errorMessages.listingInvalidCharacters");
    return false;
  } else if (Number(offerAmount.value) > userStore.getRoundedBalance) {
    errorMessageAmount.value = t("errorMessages.offersInsufficientFunds");
    return false;
  } else {
    errorMessageAmount.value = "";
    return true;
  }
};

const submitListing = () => {
  if (!validateAmount()) return;
  isLoading.value = true;
  emit("offer-set");
};

watch(offerAmount, () => {
  errorMessageAmount.value = "";
  validateAmount();
});

const calculateRemainingBalance = (amount: string = "0"): string => {
  return roundNum(
    userStore.getRoundedBalance -
      (Number(amount) && Number(amount) <= userStore.getRoundedBalance
        ? Number(amount)
        : 0)
  );
};

onMounted(async () => {
  const res = await getCollectibleHighestOffer(props.collectible.id);
  if (res) highestOffer.value = res.amount.GFAL;
});
</script>
<template>
  <form class="flex flex-col gap-y-5" @submit.prevent="submitListing">
    <div class="flex gap-4 pb-2">
      <img
        class="w-16 object-contain border border-secondary-500"
        :src="getPortraitItemImage(collectible.imageId)"
        @error="
          (e) => ((e.target as HTMLImageElement).src = getPortraitItemImage())
        " />
      <div class="flex flex-col justify-center gap-1">
        <i18n-t
          tag="p"
          class="text-neutral-300"
          keypath="main.modalOfferBegin.offerInfo"
          scope="global">
          <template #collectible>
            <span class="font-bold text-neutral-200">{{
              $t(`${collectible.translationId}.title`) +
              " " +
              $t(`${collectible.translationId}.subtitle`) +
              " #" +
              collectible.number
            }}</span>
          </template>
        </i18n-t>
      </div>
    </div>
    <div>
      <div class="flex items-baseline justify-between">
        <p class="text-xs font-bold text-neutral-300">
          {{ $t("main.modalOfferBegin.offerAmount") }}
        </p>
        <p class="text-xs font-bold text-neutral-300">
          {{
            Number(offerAmount) > 0 &&
            Number(offerAmount) < userStore.getRoundedBalance
              ? $t("main.modalTransferBegin.newBalance")
              : $t("main.modalTransferBegin.balance")
          }}:
          <span class="text-neutral-300 font-normal notranslate">
            {{ calculateRemainingBalance(offerAmount) }} GFAL
          </span>
        </p>
      </div>
      <BaseInputGFAL v-model:amount="offerAmount" />
    </div>
    <div class="flex flex-col gap-1">
      <p class="text-xs font-bold text-neutral-300">
        {{ $t("main.modalOfferBegin.offerDuration") }}:
      </p>
      <p class="text-xs text-neutral-300">
        {{ $t("main.modalOfferBegin.offerDurationAmount", { number: offerExpirationDays }) }}
      </p>
    </div>
    <div v-if="highestOffer" class="max-w-sm bg-primary-900 rounded-sm">
      <div class="flex items-center gap-3 py-2 pl-3 pr-4">
        <BaseIcon name="GFAL_token" alt="Games for a Living" class="w-4 h-4" />
        <i18n-t
          tag="p"
          class="text-rarity-exotic text-xs"
          keypath="main.modalOfferBegin.highestOffer"
          scope="global">
          <template #amount>
            <span class="font-bold">{{ highestOffer }} GFAL</span>
          </template>
        </i18n-t>
      </div>
    </div>
    <p class="text-error-300 text-xs h-4 -my-3">
      {{ errorMessageAmount }}
    </p>
    <div class="w-full mt-auto mb-2 flex items-center gap-4">
      <BaseButtonSubmit
        :loading="isLoading"
        :text="$t('main.modalOfferBegin.makeOffer')"
        :disabled="isLoading || errorMessageAmount !== '' || !offerAmount"
        data-test="button-make-offer"
        :contextual-styles="true"
        @click="trackEvent('make_offer_popup')" />
      <BaseButtonCancel
        :text="$t('main.modalOfferBegin.cancel')"
        data-test="button-dismiss-make-offer"
        :contextual-styles="true"
        @click="$emit('close')" />
    </div>
  </form>
</template>
